import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserAuthSessionStore, useAppHydrationStore } from '@/store'

interface ProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
}

/**
 * Protects routes that require authentication.
 *
 * Waits for app hydration before checking auth state to prevent
 * flash of login page when user is already authenticated but
 * session hasn't been restored from localStorage yet.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/',
}) => {
  const navigate = useNavigate()
  const { token, isSessionOpen } = useUserAuthSessionStore()
  const isHydrated = useAppHydrationStore((state) => state.isHydrated)

  useEffect(() => {
    // Only check auth after hydration is complete
    if (!isHydrated) return

    // If no token or session is not open, redirect to login
    if (!token || !isSessionOpen) {
      navigate(redirectTo, { replace: true })
    }
  }, [token, isSessionOpen, navigate, redirectTo, isHydrated])

  // Wait for hydration before making any auth decisions
  if (!isHydrated) {
    return null
  }

  // Don't render children if not authenticated
  if (!token || !isSessionOpen) {
    return null
  }

  return <>{children}</>
}
